import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";

import CartItem from "./components/CartItem";
import CallButton from "../Online/components/CallButton";

import { useSelector, useDispatch } from "react-redux";
import { setRestaurantId } from "store/actions/order";
import { decodeRestaurantId } from "util/utils";
import { loadTableId, loadRestaurant, isTakeawayEnabled } from "util/orderUtils";
import { getCurrencySymbol } from "util/currencyUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: theme.palette.grey[100],
  },
  appBar: {
    backgroundColor: theme.palette.primary.main,
  },
  toolbar: {
    minHeight: 56,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
  },
  backButton: {
    color: "#fff",
  },
  title: {
    flex: 1,
    textAlign: "center",
    fontWeight: 600,
    fontSize: 18,
    color: "#fff",
  },
  callButtonWrap: {
    color: "#fff",
  },
  itemList: {
    flex: 1,
    paddingBottom: theme.spacing(22),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 3),
    color: theme.palette.text.secondary,
  },
  summaryBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    backgroundColor: "#fff",
    borderTop: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
  },
  summaryContent: {
    padding: theme.spacing(1.5, 2),
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.25, 0),
  },
  subtotalLabel: {
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  subtotalValue: {
    fontSize: 15,
    fontWeight: 600,
  },
  surchargeLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  surchargeValue: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  disclaimerText: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    textAlign: "center",
    padding: theme.spacing(0.5, 0, 0),
  },
  checkoutButton: {
    borderRadius: 12,
    padding: theme.spacing(1.5),
    fontSize: 16,
    fontWeight: 700,
    textTransform: "none",
    marginTop: theme.spacing(1),
    boxShadow: "none",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 700,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
}));

const Cart = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [checkoutLink, setCheckoutLink] = useState("/checkout");
  const updateRestaurantId = (restaurantId) =>
    dispatch(setRestaurantId(restaurantId));
  const id = props.match.params.menuId;
  const tableId = props.match.params.tableId;
  const menuTableId = useSelector((state) => state.menu.tableId);
  const restaurantData = useSelector((state) => state.menu.restaurant);
  const [realTableId, setRealTableId] = useState(null);
  const [canDishesTakeaway, setCanDishesTakeaway] = useState(false);

  const goBack = () => {
    props.history.goBack();
  };
  const order = useSelector((state) => state.order);
  const cart = order.cart;

  const setupTableId = () => {
    if (!menuTableId) {
      setRealTableId(loadTableId(decodeRestaurantId(id)));
    } else {
      setRealTableId(menuTableId);
    }
  };

  const currencySymbol = getCurrencySymbol(
    restaurantData,
    loadRestaurant(decodeRestaurantId(id))
  );

  useEffect(() => {
    if (!realTableId) return;
    setCanDishesTakeaway(isTakeawayEnabled(realTableId, restaurantData));
  }, [realTableId]);

  const cartItemTotal = order.cart.reduce((count, curItem) => {
    return count + curItem.quantity * curItem.subTotal;
  }, 0);

  const getSurcharges = () => {
    let surcharges = [];
    if (
      restaurantData &&
      restaurantData.holidaySurcharge &&
      restaurantData.holidaySurcharge.isEnabled === true
    ) {
      let holidaySurcharge = 0;
      if (restaurantData.holidaySurcharge.isPercentage === true) {
        const percents = Number(restaurantData.holidaySurcharge.amount);
        holidaySurcharge = (cartItemTotal * percents) / 100;
      } else {
        holidaySurcharge = Number(restaurantData.holidaySurcharge.amount);
      }

      surcharges.push({
        name: restaurantData.holidaySurcharge.name,
        type: "holiday",
        amount: holidaySurcharge,
      });
    }
    return surcharges;
  };

  useEffect(() => {
    let checkoutPath = "checkout";
    if (
      restaurantData &&
      restaurantData.onlineSettings &&
      restaurantData.onlineSettings.isTillPaymentEnabled === true
    ) {
      checkoutPath = "tillCheckout";
    }
    setCheckoutLink(`/${checkoutPath}/${id}/${tableId}`);
    setupTableId();

    if (cart && cart.length === 0 && id && tableId) {
      updateRestaurantId({
        restaurantId: decodeRestaurantId(id),
        tableId: tableId,
      });
    }
  }, [props]);

  const surcharges = getSurcharges();
  const surchargeTotal = surcharges.reduce((sum, s) => sum + s.amount, 0);
  const grandTotal = cartItemTotal + surchargeTotal;

  return (
    <div className={classes.root}>
      <AppBar position="sticky" className={classes.appBar} elevation={1}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            onClick={goBack}
            edge="start"
            className={classes.backButton}
            aria-label="Go back"
          >
            <ArrowBackIosIcon />
          </IconButton>

          <Typography className={classes.title}>
            Your Order ({cart.length})
          </Typography>

          <div className={classes.callButtonWrap}>
            <CallButton />
          </div>
        </Toolbar>
      </AppBar>

      <div className={classes.itemList}>
        {cart.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body2">
              Add some items to get started
            </Typography>
          </div>
        ) : (
          <Paper elevation={0} sx={{ borderRadius: 0 }}>
            {cart.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                canDishesTakeaway={canDishesTakeaway}
                currencySymbol={currencySymbol}
              />
            ))}
          </Paper>
        )}
      </div>

      {cart.length > 0 && (
        <div className={classes.summaryBar}>
          <div className={classes.summaryContent}>
            <div className={classes.summaryRow}>
              <Typography className={classes.subtotalLabel}>
                Subtotal
              </Typography>
              <Typography className={classes.subtotalValue}>
                {currencySymbol}
                {cartItemTotal.toFixed(2)}
              </Typography>
            </div>

            {surcharges.map((surcharge, index) => (
              <div className={classes.summaryRow} key={`surcharge-${index}`}>
                <Typography className={classes.surchargeLabel}>
                  {surcharge.name}
                </Typography>
                <Typography className={classes.surchargeValue}>
                  {currencySymbol}
                  {surcharge.amount.toFixed(2)}
                </Typography>
              </div>
            ))}

            {surcharges.length > 0 && (
              <>
                <Divider sx={{ my: 0.75 }} />
                <div className={classes.totalRow}>
                  <Typography className={classes.totalLabel}>Total</Typography>
                  <Typography className={classes.totalValue}>
                    {currencySymbol}
                    {grandTotal.toFixed(2)}
                  </Typography>
                </div>
              </>
            )}

            <Typography className={classes.disclaimerText}>
              Additional charges may apply
            </Typography>

            <Button
              component={NavLink}
              to={checkoutLink}
              variant="contained"
              color="primary"
              fullWidth
              className={classes.checkoutButton}
              disableElevation
            >
              Continue to Checkout &mdash; {currencySymbol}
              {(surcharges.length > 0 ? grandTotal : cartItemTotal).toFixed(2)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
